import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import {
  getConsent,
  setConsent,
  CONSENT_STATUS,
} from "../lib/consent";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Small delay so the banner doesn't fight the page's reveal animation.
    const t = setTimeout(() => {
      if (getConsent() === CONSENT_STATUS.UNSET) setVisible(true);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const accept = () => {
    setConsent(CONSENT_STATUS.ACCEPTED);
    setVisible(false);
  };
  const decline = () => {
    setConsent(CONSENT_STATUS.DECLINED);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
      className="fixed bottom-4 inset-x-4 z-[80] mx-auto max-w-3xl"
    >
      <div className="relative rounded-2xl glass-strong overflow-hidden p-5 sm:p-6 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)]">
        <div
          className="absolute -top-20 -right-20 w-[260px] h-[260px] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(closest-side, rgba(199,166,106,0.16), transparent 70%)",
          }}
        />
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="flex items-start gap-3 flex-1">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gold/30 bg-gold/[0.06] text-gold flex-none">
              <Cookie className="w-4 h-4" />
            </span>
            <div>
              <div className="text-[13px] uppercase tracking-[0.20em] text-gold">
                Cookies & analytics
              </div>
              <p className="mt-2 text-[13.5px] leading-[1.6] text-mute-200">
                We use a small amount of analytics to understand how this site
                is used. You can accept, decline, or read our{" "}
                <Link
                  to="/cookies"
                  className="text-gold underline-offset-4 hover:underline"
                  data-testid="cookie-banner-link-cookies"
                >
                  Cookie Policy
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy"
                  className="text-gold underline-offset-4 hover:underline"
                  data-testid="cookie-banner-link-privacy"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-none">
            <button
              type="button"
              onClick={decline}
              data-testid="cookie-banner-decline"
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.025] backdrop-blur-md px-4 py-2 text-[12.5px] tracking-[0.02em] text-mute-100 hover:border-gold/40 hover:text-gold hover:bg-gold/[0.05] transition-colors"
            >
              Decline
            </button>
            <button
              type="button"
              onClick={accept}
              data-testid="cookie-banner-accept"
              className="btn-primary py-2 px-4 text-[12.5px]"
            >
              Accept
            </button>
            <button
              type="button"
              onClick={decline}
              aria-label="Dismiss"
              data-testid="cookie-banner-close"
              className="ml-1 inline-flex items-center justify-center w-7 h-7 rounded-full text-mute-300 hover:text-gold transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
